package org.lortex.lines.common

import com.github.michaelbull.result.Err
import com.github.michaelbull.result.Ok
import com.github.michaelbull.result.Result
import com.github.michaelbull.result.mapError
import com.github.michaelbull.result.mapOrElse

data class ErrorEntry(
    val message: String,
    val location: String,
    val exn: Throwable?,
)

expect fun getLocation(): String

data class AnyError(
    val entries: List<ErrorEntry>,
) {
    constructor(value: Any) : this(
        listOf(
            ErrorEntry(
                value.toString(),
                getLocation(),
                null
            )
        )
    )

    constructor(exn: Throwable) : this(
        listOf(
            ErrorEntry(
                exn.message ?: "Unknown exception",
                getLocation(),
                exn
            )
        )
    )

    companion object {
        inline fun <reified T> new(value: Any): AnyResult<T> {
            return Err(AnyError(value))
        }
    }

    fun wrap(value: Any): AnyError {
        return AnyError(
            entries + ErrorEntry(
                value.toString(),
                getLocation(),
                null
            )
        )
    }

    fun wrap(exn: Throwable): AnyError {
        val entry = ErrorEntry(
            exn.message ?: "Unknown exception",
            getLocation(),
            exn
        )
        return AnyError(
            entries + entry
        )
    }

    override fun toString(): String {
        val builder = StringBuilder()
        for (entry in entries.reversed().withIndex()) {
            builder.append("${entry.index + 1}: ${entry.value.message}\n")
            builder.append("   at ${entry.value.location}\n")
        }
        return builder.toString()
    }
}

inline fun <reified T> err(value: Any): AnyResult<T> {
    return AnyError.new(value)
}

typealias AnyResult<T> = Result<T, AnyError>

fun <T> protect(block: () -> T): AnyResult<T> {
    return try {
        Ok(block())
    } catch (exn: Throwable) {
        Err(AnyError(exn))
    }
}

suspend fun <T> protectAsync(block: suspend () -> T): AnyResult<T> {
    return try {
        Ok(block())
    } catch (exn: Throwable) {
        Err(AnyError(exn))
    }
}

fun <T> Result<T, Throwable>.toAnyResult(): AnyResult<T> {
    return this.mapError {
        AnyError(it)
    }
}

fun <T> AnyResult<T>.context(value: Any): AnyResult<T> {
    return this.mapError {
        it.wrap(value)
    }
}

inline fun <T, E> Result<T, E>.handleError(block: (E) -> T): T {
    return this.mapOrElse(
        default = {
            block(it)
        },
        transform = {
            it
        }
    )
}

inline fun <T> AnyResult<T>.handleErrorByWrapping(
    value: Any,
    block: (AnyResult<Nothing>) -> Nothing,
): T {
    return this.handleError {
        block(Err(it.wrap(value)))
    }
}